/**
 * @file activities.ts
 * @path apps/website/src/data/activities.ts
 * @description Normalized activity data with consistent field naming.
 */

import { Activity } from "@sirbastians/types";
import { Bird, Camera, Grape, Mountain, Ship } from "lucide-react";

import {
  imageCapePoint,
  imageKirstenbosch,
  imagePenguins,
  imageTableMountain,
  imageWaterfront,
  capePointFunicular,
  hermanusWhaleWatching,
  imageStellenboschWineTour,
  robbenIslandFerry,
  sealIslandHoutBay,
  sharkCageDiving,
  slaveLodgeMuseum,
} from "@/assets/images";

// --- Icon Resolver ---
const iconMap: Record<string, React.ElementType> = {
  Mountain,
  Bird,
  Camera,
  Grape,
  Ship,
};

export const getActivityIcon = (name: string) => iconMap[name] || Mountain;

export const activitiesData: Activity[] = [
  {
    id: 1,
    slug: "table-mountain",
    title: "Table Mountain",
    category: "Adventure",
    status: "active",
    iconName: "Mountain",
    gallery: { primary: imageTableMountain, additional: [] },
    shortDescription:
      "Ascend one of the New 7 Wonders of Nature with a guided hike.",
    longDescription:
      "Experience the iconic Table Mountain from a unique perspective. Our guided hikes cater to various fitness levels, leading you through diverse fynbos vegetation to the summit for unparalleled 360-degree views of Cape Town, the ocean, and beyond. This does not include the price of the cable car ticket.",
    pricing: { amount: 950, currency: "ZAR", type: "per-person" },
    duration: "Approx. 4-5 Hours",
    estimatedDuration: 270,
    difficulty: "Moderate",
    audience: ["Hikers", "Nature Lovers", "Photographers"],
    location: "Table Mountain National Park",
    whatToBring: [
      "Comfortable hiking shoes",
      "Sunscreen",
      "Water bottle",
      "Camera",
    ],
    inclusions: ["Qualified mountain guide", "Safety briefing", "Snacks"],
    exclusions: ["Cableway ticket down", "Gratuity"],
  },
  {
    id: 2,
    slug: "stellenbosch-wine-tours",
    title: "Stellenbosch Wine Tours",
    category: "Tours",
    status: "active",
    iconName: "Grape",
    gallery: { primary: imageStellenboschWineTour, additional: [] },
    shortDescription:
      "Savor award-winning wines in the heart of the Cape Winelands.",
    longDescription:
      "A journey for the senses through the historic and scenic town of Stellenbosch. We can arrange visits to a curated selection of prestigious wine estates for tastings, cellar tours, and food pairings, all while surrounded by majestic mountains and rolling vineyards.",
    pricing: { amount: 1200, currency: "ZAR", type: "per-person" },
    duration: "Full-Day (Approx. 8 Hours)",
    estimatedDuration: 480,
    difficulty: "Easy",
    audience: ["Couples", "Gourmet Travelers", "Groups"],
    location: "Stellenbosch, Cape Winelands",
    whatToBring: ["Camera", "Sun hat", "Light jacket"],
    inclusions: ["Transport to/from estates", "Driver/guide"],
    exclusions: ["Tasting fees", "Lunch", "Wine purchases"],
  },
  {
    id: 3,
    slug: "boulders-beach-penguins",
    title: "Boulders Beach Penguins",
    category: "Nature",
    status: "active",
    iconName: "Bird",
    gallery: { primary: imagePenguins, additional: [] },
    shortDescription:
      "Get up close with a charming colony of African Penguins.",
    longDescription:
      "Visit the famous Boulders Beach, home to a protected colony of over 2,000 African Penguins. Stroll along the boardwalks that wind through the dunes and get just meters away from these delightful, waddling birds in their natural habitat.",
    pricing: { amount: 215, currency: "ZAR", type: "per-person" },
    duration: "Approx. 1 Hour",
    estimatedDuration: 90,
    difficulty: "Easy",
    audience: ["Families", "Wildlife Enthusiasts", "Everyone"],
    location: "Simon's Town",
    whatToBring: ["Camera", "Sun hat", "Comfortable walking shoes"],
    inclusions: [],
    exclusions: ["SANParks entry fee"],
  },
  {
    id: 4,
    slug: "cape-point-scenic-tours",
    title: "Cape Point Scenic Tours",
    category: "Tours",
    status: "active",
    iconName: "Camera",
    gallery: { primary: imageCapePoint, additional: [] },
    shortDescription:
      "Journey to the dramatic cliffs at the tip of the Cape Peninsula.",
    longDescription:
      "Embark on a spectacular journey along one of the world's most scenic coastal drives to the dramatic cliffs of Cape Point, with stops at Chapman's Peak Drive and the Cape of Good Hope.",
    pricing: { amount: 1500, currency: "ZAR", type: "per-person" },
    duration: "Full-Day (Approx. 8 Hours)",
    estimatedDuration: 480,
    difficulty: "Easy",
    audience: ["Sightseers", "Photographers", "First-time Visitors"],
    location: "Cape Peninsula",
    whatToBring: ["Camera", "Jacket", "Comfortable shoes"],
    inclusions: ["Private transport", "Driver/guide"],
    exclusions: ["National Park entry fees", "Lunch", "Funicular ticket"],
  },
  {
    id: 5,
    slug: "va-waterfront",
    title: "V&A Waterfront",
    category: "Leisure",
    status: "active",
    iconName: "Ship",
    gallery: { primary: imageWaterfront, additional: [] },
    shortDescription:
      "Explore world-class shopping, dining, and entertainment.",
    longDescription:
      "As a bustling hub of activity, the V&A Waterfront offers something for everyone. From the Two Oceans Aquarium and boat trips around the harbour to endless shops and world-class restaurants, it's a perfect destination for a day of leisure.",
    pricing: { amount: 0, currency: "ZAR", type: "per-person" },
    duration: "Flexible",
    estimatedDuration: 180,
    difficulty: "Easy",
    audience: ["Families", "Shoppers", "Foodies"],
    location: "V&A Waterfront",
    whatToBring: ["Comfortable walking shoes", "Camera"],
    inclusions: ["Return transport"],
    exclusions: ["All meals, shopping, and activity costs"],
  },
  {
    id: 6,
    slug: "kirstenbosch-gardens",
    title: "Kirstenbosch Gardens",
    category: "Nature",
    status: "active",
    iconName: "Camera",
    gallery: { primary: imageKirstenbosch, additional: [] },
    shortDescription:
      "Discover indigenous flora in a world-renowned botanical garden.",
    longDescription:
      "Acclaimed as one of the great botanic gardens of the world, Kirstenbosch is a haven of peace and tranquility on the eastern slopes of Table Mountain. Explore themed gardens, art sculptures, and the famous 'Boomslang' canopy walkway.",
    pricing: { amount: 250, currency: "ZAR", type: "per-person" },
    duration: "2-3 Hours",
    estimatedDuration: 210,
    difficulty: "Easy",
    audience: ["Nature Lovers", "Families", "Picnickers"],
    location: "Kirstenbosch National Botanical Garden",
    whatToBring: ["Walking shoes", "Camera", "Picnic basket"],
    inclusions: [],
    exclusions: ["Garden entry fee", "Food and drinks"],
  },
  {
    id: 7,
    slug: "shark-cage-diving",
    title: "Shark Cage Diving",
    category: "Adventure",
    status: "active",
    iconName: "Ship",
    gallery: { primary: sharkCageDiving, additional: [] },
    shortDescription:
      "Get up close with the ocean's most magnificent predator, the great white shark.",
    longDescription:
      "An exhilarating boat trip off the coast of Gansbaai, one of the world's premier shark cage diving locations. Experience the thrill of seeing Great White Sharks in their natural habitat from the safety of a cage.",
    pricing: { amount: 3900, currency: "ZAR", type: "per-person" },
    duration: "3-hour Boat Trip",
    estimatedDuration: 180,
    difficulty: "Moderate",
    audience: [
      "Adrenaline Seekers",
      "Marine Life Enthusiasts",
      "Adventurous Travelers",
    ],
    location: "Kleinbaai / Gansbaai",
    whatToBring: ["Swimsuit", "Towel", "Sunscreen", "Camera"],
    inclusions: ["Boat trip", "All diving equipment", "On-board refreshments"],
    exclusions: ["Transport to Gansbaai", "Gratuity"],
  },
  {
    id: 8,
    slug: "robben-island-tour",
    title: "Robben Island Tour",
    category: "Tours",
    status: "active",
    iconName: "Ship",
    gallery: { primary: robbenIslandFerry, additional: [] },
    shortDescription:
      "A profound journey into South Africa's history of struggle and resilience.",
    longDescription:
      "Take a ferry to the UNESCO World Heritage Site of Robben Island. A guided tour, often led by a former political prisoner, will take you through the maximum-security prison, including a viewing of Nelson Mandela's former cell.",
    pricing: { amount: 600, currency: "ZAR", type: "per-person" },
    duration: "Approx. 3.5 Hours (including ferry)",
    estimatedDuration: 210,
    difficulty: "Easy",
    audience: ["History Buffs", "Students", "Culture Enthusiasts"],
    location: "Robben Island",
    whatToBring: ["Jacket", "Camera", "Comfortable shoes"],
    inclusions: [
      "Return ferry trip",
      "Guided bus tour of the island",
      "Tour of the prison",
    ],
    exclusions: ["Transport to the ferry terminal", "Gratuity"],
  },
  {
    id: 9,
    slug: "hout-bay-seal-island",
    title: "Hout Bay Seal Island Boat Trip",
    category: "Nature",
    status: "active",
    iconName: "Ship",
    gallery: { primary: sealIslandHoutBay, additional: [] },
    shortDescription:
      "Boat trip to see thousands of Cape Fur Seals in their natural habitat.",
    longDescription:
      "Embark on an exciting boat trip from Hout Bay Harbour to Duiker Island, home to thousands of Cape Fur Seals. Watch these playful creatures bask on the rocks and swim in the waters around the island. The trip also offers stunning views of the coastline and Chapman's Peak.",
    pricing: { amount: 150, currency: "ZAR", type: "per-person" },
    duration: "Approx. 45 Minutes",
    estimatedDuration: 45,
    difficulty: "Easy",
    audience: ["Families", "Wildlife Enthusiasts", "Photographers"],
    location: "Hout Bay Harbour",
    whatToBring: ["Camera", "Jacket", "Sunglasses"],
    inclusions: ["Boat trip", "Guide commentary"],
    exclusions: ["Transport to Hout Bay", "Gratuity"],
  },
  {
    id: 10,
    slug: "cape-point-funicular",
    title: "Cape Point Funicular",
    category: "Leisure",
    status: "active",
    iconName: "Mountain",
    gallery: { primary: capePointFunicular, additional: [] },
    shortDescription: "The Flying Dutchman Funicular to Cape Point Lighthouse.",
    longDescription:
      "A cable-pulled railway and the only one of its kind in Africa, that takes visitors from the lower station at the car park up to the upper lighthouse at Cape Point. It is named after the legendary Flying Dutchman ghost ship and offers a quick, easy, and scenic way to access the lighthouse and stunning views of the coastline",
    pricing: { amount: 115, currency: "ZAR", type: "per-person" },
    duration: "5 Minutes Each Way",
    estimatedDuration: 10,
    difficulty: "Easy",
    audience: ["Everyone", "Families", "Seniors"],
    location: "Cape Point",
    whatToBring: ["Camera", "Sun hat"],
    inclusions: ["Return funicular ride"],
    exclusions: ["Cape Point Nature Reserve entry fee"],
  },
  {
    id: 11,
    slug: "hermanus-coastal-tour",
    title: "Hermanus Coastal Tour",
    category: "Nature",
    status: "seasonal",
    iconName: "Camera",
    gallery: { primary: hermanusWhaleWatching, additional: [] },
    shortDescription:
      "Visit the whale watching capital of South Africa with stunning coastal views.",
    longDescription:
      "Hermanus is renowned as one of the world's best land-based whale watching destinations. During whale season (June-November), Southern Right Whales come close to shore to calve and nurse their young. Enjoy scenic coastal walks along cliff paths with breathtaking ocean views.",
    pricing: { amount: 0, currency: "ZAR", type: "per-person" },
    duration: "1-2 Hours",
    estimatedDuration: 90,
    difficulty: "Easy",
    audience: ["Wildlife Enthusiasts", "Photographers", "Nature Lovers"],
    location: "Hermanus",
    whatToBring: ["Camera", "Binoculars", "Jacket"],
    inclusions: ["Included in tour"],
    exclusions: ["Personal expenses"],
  },
  {
    id: 12,
    slug: "stony-point-penguins",
    title: "Stony Point Penguin Colony",
    category: "Nature",
    status: "active",
    iconName: "Bird",
    gallery: { primary: imagePenguins, additional: [] },
    shortDescription: "Visit an African penguin colony at Betty's Bay.",
    longDescription:
      "Stony Point in Betty's Bay is home to a thriving colony of African Penguins. Walk along wooden boardwalks that wind through the penguin habitat, offering excellent viewing and photography opportunities. This smaller, less crowded alternative to Boulders Beach provides an intimate wildlife experience.",
    pricing: { amount: 45, currency: "ZAR", type: "per-person" },
    duration: "Approx. 1 Hour",
    estimatedDuration: 60,
    difficulty: "Easy",
    audience: ["Families", "Wildlife Enthusiasts", "Photographers"],
    location: "Betty's Bay",
    whatToBring: ["Camera", "Sun hat", "Comfortable walking shoes"],
    inclusions: ["Entrance fee included in tour"],
    exclusions: [],
  },
  {
    id: 13,
    slug: "harold-porter-gardens",
    title: "Harold Porter Botanical Gardens",
    category: "Nature",
    status: "active",
    iconName: "Camera",
    gallery: { primary: imageKirstenbosch, additional: [] },
    shortDescription: "Fynbos botanical garden with coastal mountain views.",
    longDescription:
      "Harold Porter National Botanical Garden showcases the unique fynbos flora of the Kogelberg region. Nestled between mountains and sea, the garden features diverse plant species, a waterfall trail, and stunning coastal views. It's a peaceful haven for nature lovers and photographers.",
    pricing: { amount: 60, currency: "ZAR", type: "per-person" },
    duration: "1-2 Hours",
    estimatedDuration: 90,
    difficulty: "Easy",
    audience: ["Nature Lovers", "Photographers", "Botanists"],
    location: "Betty's Bay",
    whatToBring: ["Walking shoes", "Camera", "Water bottle"],
    inclusions: ["Entrance fee included in tour"],
    exclusions: [],
  },
  {
    id: 14,
    slug: "iziko-slave-lodge",
    title: "Iziko Slave Lodge Museum",
    category: "Tours",
    status: "active",
    iconName: "Mountain",
    gallery: { primary: slaveLodgeMuseum, additional: [] },
    shortDescription:
      "Historical museum documenting South Africa's slave history.",
    longDescription:
      "The Iziko Slave Lodge is one of South Africa's oldest buildings and most significant heritage sites. Originally built in 1679 to house enslaved people, it now serves as a museum documenting the history of slavery at the Cape and its lasting impact on South African society.",
    pricing: { amount: 80, currency: "ZAR", type: "per-person" },
    duration: "Approx. 1 Hour",
    estimatedDuration: 60,
    difficulty: "Easy",
    audience: ["History Buffs", "Students", "Culture Enthusiasts"],
    location: "Cape Town City Centre",
    whatToBring: ["Camera", "Notebook"],
    inclusions: ["Entrance fee included in tour"],
    exclusions: [],
  },
];
